import type { routes } from '@semios/app-platform-banyan-route-definitions'

type TBatchRequest = routes.IrrigationSchedulerPlanningData.Request

type TBatchResponse = routes.IrrigationSchedulerPlanningData.Response

export type TPlanningProperty = { propertyId: number; blockIds: number[]; zoneIds: number[] }

type TBatchLimits = { maxItems: number; maxZones: number; maxBlocks: number }

// The server pairs each zone only with blocks of its own property, so the union does not mix properties.
export const buildBatchRequest = ({
  properties,
  dateFrom,
  dateTo,
}: {
  properties: Pick<TPlanningProperty, 'blockIds' | 'zoneIds'>[]
  dateFrom: string
  dateTo: string
}): TBatchRequest => ({
  blockIds: [...new Set(properties.flatMap((property) => property.blockIds))],
  irrigationZoneIds: [...new Set(properties.flatMap((property) => property.zoneIds))],
  dateFrom,
  dateTo,
})

export const splitBatchResponse = (
  entries: { key: string; zoneIds: number[] }[],
  response: TBatchResponse,
): Map<string, TBatchResponse> =>
  new Map(
    entries.map(({ key, zoneIds }) => {
      const ownZoneIds = new Set(zoneIds)

      return [key, { dailyEt: response.dailyEt.filter((zone) => ownZoneIds.has(zone.zone_id)) }]
    }),
  )

export const groupWithinLimits = <TItem extends Pick<TPlanningProperty, 'blockIds' | 'zoneIds'>>(
  items: TItem[],
  { maxItems, maxZones, maxBlocks }: TBatchLimits,
): TItem[][] => {
  const groups: { items: TItem[]; zones: number; blocks: number }[] = []

  let current: (typeof groups)[number] | undefined

  items.forEach((item) => {
    if (
      current &&
      current.items.length < maxItems &&
      current.zones + item.zoneIds.length <= maxZones &&
      current.blocks + item.blockIds.length <= maxBlocks
    ) {
      current.items.push(item)

      current.zones += item.zoneIds.length

      current.blocks += item.blockIds.length
    } else {
      current = { items: [item], zones: item.zoneIds.length, blocks: item.blockIds.length }

      groups.push(current)
    }
  })

  return groups.map((group) => group.items)
}
